import { fmtDateTime } from '@/lib/dates.js';
import { useState, useMemo } from 'react';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Info, Loader2, Search } from 'lucide-react';
import { compliance, adminUsers, getAuthToken, type AccessFilters } from '@/api/client';
import { humanRole, humanResourceType } from '@/lib/humanise';
import { MultiSelectSearch, type MultiSelectOption } from '@/components/ui/MultiSelectSearch';
import { toast } from 'sonner';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


async function downloadCsv(url: string, filename: string) {
  const token = getAuthToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

type SearchFilters = Omit<AccessFilters, 'page' | 'limit'>;
type SortKey = 'timestamp' | 'user' | 'role' | 'resource' | 'action';
type SortDir = 'asc' | 'desc';

const PAGE_SIZES = [25, 50, 100] as const;

const RESOURCE_TYPE_CODES = [
  'subject', 'visit', 'ecrf_form', 'phi_grant', 'audit_log', 'access_log',
  'csr_export', 'query', 'remote_view_session', 'study', 'site',
] as const;

const RESOURCE_TYPE_OPTIONS: MultiSelectOption[] = RESOURCE_TYPE_CODES.map((rt) => ({
  value: rt,
  label: humanResourceType(rt),
  sublabel: rt,
}));

function localToIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}
function isoToLocal(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AccessLogTab() {
  // Client rule, 2026-10-09: an IP address is masked for everyone except
  // site admins and the platform tier. The server already nulls the value
  // for those callers — this hides the column too, so nobody stares at a
  // row of dashes wondering whether the data is missing or withheld.
  const showIp = useMyPermissions().holdsOperation('compliance.view_identifiers');

  const [filters, setFilters] = useState<SearchFilters>({});
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedResourceTypes, setSelectedResourceTypes] = useState<string[]>([]);
  const [fromLocal, setFromLocal] = useState('');
  const [toLocal, setToLocal] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [exporting, setExporting] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });
  const usersById = useMemo(() => {
    const m = new Map<string, { full_name: string; email: string }>();
    users.data?.rows.forEach((u) => m.set(u.user_id, { full_name: u.full_name, email: u.email }));
    return m;
  }, [users.data]);
  const userOptions = useMemo<MultiSelectOption[]>(
    () =>
      (users.data?.rows ?? []).map((u) => ({
        value: u.user_id,
        label: u.full_name || u.email.split('@')[0] || u.email,
        sublabel: u.email,
      })),
    [users.data],
  );

  const queryFilters: AccessFilters = { ...filters, page, limit: pageSize };

  const q = useQuery({
    queryKey: ['access-log', queryFilters],
    queryFn: () => compliance.listAccessLog(queryFilters),
  });

  function applyFilters() {
    setFilters({
      user_id: selectedUserIds.length > 0 ? selectedUserIds : undefined,
      resource_type: selectedResourceTypes.length > 0 ? selectedResourceTypes : undefined,
      from: localToIso(fromLocal),
      to: localToIso(toLocal),
    });
    setPage(1);
  }

  function clearFilters() {
    setSelectedUserIds([]); setSelectedResourceTypes([]); setFromLocal(''); setToLocal('');
    setFilters({}); setPage(1);
  }

  function handlePageSizeChange(size: number) { setPageSize(size); setPage(1); }

  async function handleExport() {
    setExporting(true);
    try {
      const url = compliance.exportAccessLogUrl(filters);
      await downloadCsv(url, `access-log-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Access log exported');
    } catch (err) {
      toast.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  }

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'timestamp' ? 'desc' : 'asc'); }
  }
  function sortIndicator(k: SortKey) {
    if (sortKey !== k) return null;
    return <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  const sortedRows = useMemo(() => {
    const rows = q.data?.rows ?? [];
    const cmp = (a: typeof rows[number], b: typeof rows[number]): number => {
      let v = 0;
      switch (sortKey) {
        case 'timestamp':
          v = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          break;
        case 'user': {
          const an = usersById.get(a.user_id ?? '');
          const bn = usersById.get(b.user_id ?? '');
          v = (an?.full_name || an?.email || a.user_id || '').localeCompare(bn?.full_name || bn?.email || b.user_id || '');
          break;
        }
        case 'role':
          v = humanRole(a.user_role_at_time).localeCompare(humanRole(b.user_role_at_time));
          break;
        case 'resource':
          v = humanResourceType(a.resource_type).localeCompare(humanResourceType(b.resource_type));
          break;
        case 'action':
          v = (a.action_detail || '').localeCompare(b.action_detail || '');
          break;
      }
      return sortDir === 'asc' ? v : -v;
    };
    return [...rows].sort(cmp);
  }, [q.data, sortKey, sortDir, usersById]);

  const hasMore = q.data?.hasMore ?? false;
  const total = q.data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : page;

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="label text-xs" htmlFor="al-user">User</label>
            <MultiSelectSearch
              id="al-user"
              options={userOptions}
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              placeholder="Any user"
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="al-resource">Resource type</label>
            <MultiSelectSearch
              id="al-resource"
              options={RESOURCE_TYPE_OPTIONS}
              value={selectedResourceTypes}
              onChange={setSelectedResourceTypes}
              placeholder="Any resource type"
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="al-from">From</label>
            <input id="al-from" type="datetime-local" className="input"
              value={fromLocal || isoToLocal(filters.from)}
              onChange={(e) => setFromLocal(e.target.value)} />
          </div>
          <div>
            <label className="label text-xs" htmlFor="al-to">To</label>
            <input id="al-to" type="datetime-local" className="input"
              value={toLocal || isoToLocal(filters.to)}
              onChange={(e) => setToLocal(e.target.value)} />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={applyFilters} className="btn-secondary">
            <Search className="h-4 w-4" /> Search
          </button>
          <button type="button" onClick={clearFilters} className="btn-ghost text-xs">Clear</button>
          <button type="button" onClick={handleExport} disabled={exporting || !q.data?.rows?.length} className="btn-secondary">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </button>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs text-ink-muted" htmlFor="al-page-size">Rows per page:</label>
            <select id="al-page-size" className="input py-1 text-xs w-20"
              value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
              {PAGE_SIZES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {q.isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {q.isError && (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(q.error)}`}>
            {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{errorText(q.error, 'Failed to load access log.')}</span>
          </div>
        )}
        {!q.isLoading && q.data && q.data.rows.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-muted">No access events match the filters.</div>
        )}
        {q.data && q.data.rows.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('timestamp')}>
                      <span className="inline-flex items-center gap-1">Timestamp {sortIndicator('timestamp')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('user')}>
                      <span className="inline-flex items-center gap-1">User {sortIndicator('user')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('role')}>
                      <span className="inline-flex items-center gap-1">Role {sortIndicator('role')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('resource')}>
                      <span className="inline-flex items-center gap-1">Resource {sortIndicator('resource')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('action')}>
                      <span className="inline-flex items-center gap-1">Action {sortIndicator('action')}</span>
                    </th>
                    {showIp && <th className="px-3 py-2">IP</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r) => {
                    const u = r.user_id ? usersById.get(r.user_id) : null;
                    return (
                      <tr key={r.access_id} className="border-b border-surface-border last:border-0 hover:bg-surface-muted/40">
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-secondary">
                          {fmtDateTime(r.timestamp)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {u ? (
                            <>
                              <div className="font-medium text-ink-primary">{u.full_name || u.email.split('@')[0]}</div>
                              <div className="text-[10px] text-ink-muted">{u.email}</div>
                            </>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{r.user_role_at_time ? humanRole(r.user_role_at_time) : '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className="badge badge-neutral">{humanResourceType(r.resource_type)}</span>
                        </td>
                        <td className="px-3 py-2 text-xs font-medium">{r.action_detail}</td>
                        {showIp && (
                          <td className="px-3 py-2 font-mono text-xs text-ink-muted">{r.ip ?? '—'}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-surface-border px-4 py-3">
              <span className="text-xs text-ink-muted">
                Page {page} of {totalPages} &middot; {total.toLocaleString()} total records
                <span className="ml-2 italic">(sort applies to this page only)</span>
              </span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setPage(1)} disabled={page <= 1}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="First page">
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Previous page">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-xs font-medium text-ink-secondary">{page}</span>
                <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!hasMore}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Next page">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages || total === 0}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Last page">
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
